import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Save, Printer, Eye, Receipt, MonitorSmartphone, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import PrintableTicket, { printTicket } from "../components/PrintableTicket";
import { ReceiptTab } from "../components/settings/ReceiptTab";
import { PosTab } from "../components/settings/PosTab";
import { RiskTab } from "../components/settings/RiskTab";

const DEMO_TICKET = {
  ticket_number: "BR01-20260908-000075",
  branch_code: "BR01",
  user_name: "cajero@sally.do",
  created_at: new Date().toISOString(),
  status: "active",
  total_amount: 35.0,
  total_prize: 0,
  items: [
    { id: "d1", game_name: "Nacional Noche", play_type_name: "Quiniela", display: "07", numbers: ["07"], amount: 10, draw_time: "20:55" },
    { id: "d2", game_name: "Nacional Noche", play_type_name: "Quiniela", display: "03", numbers: ["03"], amount: 10, draw_time: "20:55" },
    { id: "d3", game_name: "Nacional Noche", play_type_name: "Palé",     display: "03-07", numbers: ["03","07"], amount: 5, draw_time: "20:55" },
    { id: "d4", game_name: "Nacional Noche", play_type_name: "Palé",     display: "70-03", numbers: ["70","03"], amount: 5, draw_time: "20:55" },
    { id: "d5", game_name: "Nacional Noche", play_type_name: "Palé",     display: "07-45", numbers: ["07","45"], amount: 5, draw_time: "20:55" },
  ],
};

const SAVE_KEYS = [
  "risk_alert_enabled", "auto_suggest_limit", "receipt_paper_size",
  "receipt_footer_line1", "receipt_footer_line2", "receipt_footer_line3",
  "receipt_show_copy_mark", "pos_hide_closed_games", "pos_mobile_style",
  "receipt_styles", "receipt_title_text", "receipt_show_logo", "receipt_show_serial",
  "receipt_show_code", "receipt_show_legend", "receipt_show_qr", "receipt_qr_size", "receipt_logo_size",
];

const TABS = [
  { value: "receipt", label: "Recibo", icon: Receipt },
  { value: "pos", label: "Punto de Venta", icon: MonitorSmartphone },
  { value: "risk", label: "Riesgo", icon: ShieldAlert },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("receipt");
  const [saving, setSaving] = useState(false);
  const [quickAmountsText, setQuickAmountsText] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setS(data);
      setQuickAmountsText((data.pos_quick_amounts || []).join(", "));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const amounts = quickAmountsText.split(/[,\s]+/).map((v) => parseFloat(v)).filter((n) => Number.isFinite(n) && n > 0);
      const payload = Object.fromEntries(SAVE_KEYS.filter((k) => s[k] !== undefined).map((k) => [k, s[k]]));
      const { data } = await api.put("/settings", {
        ...payload,
        risk_alert_pct: parseFloat(s.risk_alert_pct),
        risk_alert_min_amount: parseFloat(s.risk_alert_min_amount),
        receipt_business_name: (s.receipt_business_name || "").trim() || "BANCA POS",
        receipt_website: (s.receipt_website || "").trim(),
        pos_quick_amounts: amounts.length > 0 ? amounts : [5, 10, 25, 50, 100],
      });
      setS(data);
      setQuickAmountsText((data.pos_quick_amounts || []).join(", "));
      toast.success("Configuración guardada");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader title="Configuración" subtitle="Recibo, punto de venta y motor de riesgo" testId="settings-header" />
      <div className="p-4 sm:p-8 max-w-6xl">
        {!s ? (
          <div className="text-slate-500 text-sm">Cargando…</div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-6 h-auto p-1 bg-white border border-slate-200 rounded-sm flex w-full sm:w-auto sm:inline-flex" data-testid="settings-tabs">
              {TABS.map(({ value, label, icon: Icon }) => (
                <TabsTrigger key={value} value={value} className="flex-1 sm:flex-none rounded-sm px-3 sm:px-5 py-2 text-xs sm:text-sm font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid={`settings-tab-${value}`}>
                  <Icon className="w-4 h-4 mr-1.5 hidden sm:inline" /> {label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className={`grid grid-cols-1 gap-6 ${tab === "receipt" ? "xl:grid-cols-[minmax(0,1fr)_360px]" : ""}`}>
              <div className="space-y-6 min-w-0">
                <TabsContent value="receipt" className="mt-0"><ReceiptTab s={s} setS={setS} /></TabsContent>
                <TabsContent value="pos" className="mt-0"><PosTab s={s} setS={setS} quickAmountsText={quickAmountsText} setQuickAmountsText={setQuickAmountsText} /></TabsContent>
                <TabsContent value="risk" className="mt-0"><RiskTab s={s} setS={setS} /></TabsContent>
                <SaveBar tab={tab} s={s} saving={saving} onSave={save} />
              </div>
              {tab === "receipt" && <ReceiptPreview settings={s} />}
            </div>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function SaveBar({ tab, s, saving, onSave }) {
  return (
    <div className="flex flex-wrap justify-between items-center gap-2 sticky bottom-4 z-10">
      {tab === "receipt" ? (
        <Button
          onClick={() => { printTicket({ ...DEMO_TICKET, created_at: new Date().toISOString() }, { settings: s, isReprint: false }); toast.info("Enviando ticket de prueba…"); }}
          variant="outline" className="rounded-sm border-slate-300 h-11 px-4 bg-white" data-testid="settings-test-print"
        >
          <Printer className="w-4 h-4 mr-2" /> Imprimir prueba
        </Button>
      ) : <span />}
      <Button onClick={onSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 rounded-sm font-bold h-11 px-6" data-testid="settings-save">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar"}
      </Button>
    </div>
  );
}

function ReceiptPreview({ settings }) {
  return (
    <aside className="xl:sticky xl:top-6 xl:self-start" data-testid="receipt-preview-panel">
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
          <Eye className="w-4 h-4 text-slate-700" />
          <div>
            <div className="text-xs font-bold text-slate-900 uppercase tracking-wider">Vista Previa del Recibo</div>
            <div className="text-[10px] text-slate-500">En vivo · sin guardar</div>
          </div>
        </div>
        <div className="bg-gradient-to-b from-slate-100 to-slate-200 p-4 flex justify-center">
          <div className="bg-white shadow-md border border-slate-300 overflow-hidden max-w-full">
            <PrintableTicket ticket={DEMO_TICKET} settings={settings} isReprint={false} />
          </div>
        </div>
        <div className="px-4 py-2 border-t border-slate-200 text-[10px] text-slate-500 text-center">
          Tamaño simulado {settings.receipt_paper_size || "80mm"} · ticket de ejemplo
        </div>
      </div>
    </aside>
  );
}
